/**
 * News Page Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive news article display page with advanced features:
 * - Dynamic news fetching by ID from URL parameters
 * - News navigation with previous/next functionality
 * - Multi-media support (video, audio, PDF, images)
 * - Video streaming with fallback to blob downloads
 * - Audio player integration with context management
 * - PDF modal integration with fullscreen support
 * - Image loading and caching system
 * - Reading time calculation
 * - URL copying functionality
 * - Repository document link processing
 * - Escape key modal closing
 * - Responsive design for various screen sizes
 * 
 * Features:
 * - Smart news data caching and fresh fetching
 * - Article type detection (featured vs regular)
 * - Navigation with article preview
 * - Rich text content processing
 * - Media streaming with multiple fallback options
 * - Audio context integration for podcasts
 * - Dynamic content loading states
 * - Error handling for failed media loads
 * - Real-time URL parameter synchronization
 * 
 * @returns {JSX.Element} News page component with comprehensive article display
 */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import 'tippy.js/dist/tippy.css';
import '../modals/NewsModal.css'; // Reusing NewsModal styles
import './News.css';
import { cleanJson, addTargetBlankToLinks, removeFirstBoldTag, replaceCss } from '../utils/textUtils';
import apiHelper from '../utils/apiHelper';
import PDFModal from '../modals/PDFModal';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Tippy from '@tippyjs/react';

/**
 * News component for displaying detailed news articles
 * with navigation, media support, and interactive features
 */
function News() {
    const { id: newsId, type: articleType } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    
    // News data state
    const [newsData, setNewsData] = useState(null);
    const [allNewsData, setAllNewsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [navigationLoading, setNavigationLoading] = useState(true);
    const [error, setError] = useState(null);
    // Add retry state
    const [retryAttempt, setRetryAttempt] = useState(0);
    const [isRetrying, setIsRetrying] = useState(false);
    
    // Media states
    const [articleImage, setArticleImage] = useState(null);
    const [videoSource, setVideoSource] = useState(null);
    const [podcastSource, setPodcastSource] = useState(null);
    const [pdfSource, setPdfSource] = useState(null);
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const [isAudioLoading, setIsAudioLoading] = useState(true);
    const [isVideoExpanded, setIsVideoExpanded] = useState(false);
    const [isVideoEmbedded, setIsVideoEmbedded] = useState(false);
    const [isPodcastEmbedded, setIsPodcastEmbedded] = useState(false);
    
    // Modal states
    const [showImageModal, setShowImageModal] = useState(false);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [showRepositoryPdfModal, setShowRepositoryPdfModal] = useState(false);
    const [repositoryPdfData, setRepositoryPdfData] = useState(null);
    
    // Copy URL state
    const [copyUrlText, setCopyUrlText] = useState('Copy URL');
    
    // Reading time calculation
    const calculateReadingTime = (text) => {
        if (!text) return 0;
        const wordsPerMinute = 200;
        const cleanText = text.replace(/<[^>]*>/g, ''); // Remove HTML tags
        const wordCount = cleanText.split(/\s+/).filter(word => word.length > 0).length;
        return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
    };
    
    // Navigation states
    const [nextNewsId, setNextNewsId] = useState(null);
    const [previousNewsId, setPreviousNewsId] = useState(null);
    
    // Refs
    const currentBlobUrl = useRef(null);
    const loadingRef = useRef(false);

    // Add retry function
    const handleRetry = async () => {
        setIsRetrying(true);
        setError(null);
        setLoading(true);
        setNavigationLoading(true);
        setRetryAttempt(prev => prev + 1);
        
        try {
            // Clear any cached data and force fresh fetch
            setNewsData(null);
            setAllNewsData([]);
            
            // Wait a moment before retrying to allow for potential API sync
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Force fresh data fetch by calling fetchFreshData directly
            await fetchFreshData();
            
        } catch (err) {
            console.error(`Retry attempt ${retryAttempt + 1} failed:`, err);
            setError(err.message);
            setLoading(false);
            setNavigationLoading(false);
        } finally {
            setIsRetrying(false);
        }
    };

    // Helper functions moved outside useEffect for retry functionality
    const findNewsById = (allNews, searchId, searchType = null) => {
        
        
        // Log first few IDs for debugging
        const firstFewIds = allNews.slice(0, 10).map(item => ({
            id: item.id,
            type: typeof item.id,
            title: item.title?.substring(0, 50) + '...',
            isSubmitted: Boolean(item.isSubmittedArticle),
            isWeekly: Boolean(item.isWeeklyNews)
        }));
        
        
        const foundItem = allNews.find(item => {
            const itemId = item.id;
            
            // First check ID match
            let idMatches = false;
            if (itemId === searchId) {
                idMatches = true;
            } else if (itemId === parseInt(searchId)) {
                idMatches = true;
            } else if (String(itemId) === String(searchId)) {
                idMatches = true;
            }
            
            if (!idMatches) return false;
            
            // If we have a specific article type, check it matches
            if (searchType) {
                switch (searchType.toLowerCase()) {
                    case 'submitted':
                        return Boolean(item.isSubmittedArticle);
                    case 'weekly':
                        return Boolean(item.isWeeklyNews);
                    default:
                        return true; // Fallback to any match
                }
            } else {
                // No type specified - return first match (legacy behavior)
                return true;
            }
        });
        
        // foundItem will be undefined if not found
        
        return foundItem;
    };

    const setupNavigation = (allNews, currentNews) => {
        // Filter out intro articles and unpublished articles from navigation
        const navigationNews = allNews.filter(item => {
            // Exclude articles with "Intro" in title or weeklyCategory
            const isIntro = (item.title && item.title.toLowerCase().includes('intro')) ||
                          (item.weeklyCategory && item.weeklyCategory.toLowerCase() === 'intro') ||
                          (item.title === 'Intro');
            
            // Exclude unpublished articles
            const isUnpublished = item.published === false;
            
            return !isIntro && !isUnpublished;
        });
        
        
        
        const currentIndex = navigationNews.findIndex(item => item.id === currentNews.id);
        if (currentIndex > 0) {
            setPreviousNewsId(navigationNews[currentIndex - 1].id);
        } else {
            setPreviousNewsId(null);
        }
        if (currentIndex < navigationNews.length - 1) {
            setNextNewsId(navigationNews[currentIndex + 1].id);
        } else {
            setNextNewsId(null);
        }
        
        
    };

    const fetchFreshData = async () => {
        // Use direct graph API instead of JSON files for real-time data
        setLoading(true);
        setNavigationLoading(true);
        
        try {
            
            // Use your existing /getAllNews endpoint for real-time data
            const response = await apiHelper.fetchJSONData('getAllNews');
            
            if (response && response.news && Array.isArray(response.news)) {
                const allNews = response.news;
                
                
                // Debug: Log sample of IDs
                const sampleIds = allNews.slice(0, 10).map(item => ({
                    id: item.id,
                    type: typeof item.id,
                    title: item.title?.substring(0, 50) + '...',
                    newsType: item.newsType || 'Unknown'
                }));
                
                // Find current article
                const currentNews = findNewsById(allNews, newsId, articleType);
                
                if (currentNews) {
                    // Check if the article is published
                    if (currentNews.published === false) {
                        throw new Error('This article is not yet published and cannot be accessed');
                    }
                    setNewsData(currentNews);
                    setAllNewsData(allNews);
                    setupNavigation(allNews, currentNews);
                    setLoading(false);
                    setNavigationLoading(false);
                } else {
                    throw new Error(`News item with ID ${newsId}${articleType ? ` (type: ${articleType})` : ''} not found in graph data. Total articles: ${allNews.length}. Available IDs: ${allNews.map(item => item.id).slice(0, 10).join(', ')}${allNews.length > 10 ? '...' : ''}`);
                }
            } else {
                throw new Error('Invalid response from getAllNews API');
            }
        } catch (error) {
            throw error;
        }
    };

    // Memoize extracted data - map graph API fields to component expectations
    const { title, description, summary, video, author, firm, podcast, file, image } = useMemo(() => {
        if (!newsData) {
            return {};
        }
        
        const extracted = {
            title: newsData.title,
            // Use the fields as provided by the API (no more complex fallbacks needed)
            description: newsData.description || newsData.content,
            summary: newsData.summary,
            video: newsData.video,
            author: newsData.author || newsData.submittedAuthor || newsData.submittedBy || newsData.authorName || newsData.contributorName || '', // Handle both weekly and submitted articles
            firm: newsData.submittedCompany || newsData.firm || newsData.company || newsData.organization || newsData.authorCompany || '',  // For submitted articles
            podcast: newsData.audio || newsData.podcast,  // Graph API uses 'audio' for podcasts, but modal might use 'podcast'
            file: newsData.pdf || newsData.fileName || newsData.file,  // Multiple fallbacks for documents
            image: newsData.image
        };
                
        return extracted;
    }, [newsData, location.state]);

    // Progressive loading: prioritize current article, then load navigation data
    useEffect(() => {
        const fetchNewsData = async () => {
            try {
                // Check if we have pre-loaded data from navigation
                const passedData = location.state?.allNewsData;
                const passedCurrentNews = location.state?.currentNews;
                
                // First priority: Both current news and full data passed (from navigation)
                if (passedData && passedCurrentNews) {
                    
                    
                    // Verify the passed current news matches what we're looking for
                    if (String(passedCurrentNews.id) === String(newsId)) {
                        // Check if the article is published
                        if (passedCurrentNews.published === false) {
                            throw new Error('This article is not yet published and cannot be accessed');
                        }
                        
                        // Use the passed data immediately
                        setNewsData(passedCurrentNews);
                        setAllNewsData(passedData);
                        setLoading(false);
                        setNavigationLoading(false);
                        
                        // Set up navigation immediately
                        setupNavigation(passedData, passedCurrentNews);
                        return;
                    } else {
                        // Passed current news ID does not match requested ID, fetching fresh...
                    }
                }
                
                // Second priority: Just current news passed (from modal "open in dedicated page")
                if (passedCurrentNews && String(passedCurrentNews.id) === String(newsId)) {
                    // Check if the article is published
                    if (passedCurrentNews.published === false) {
                        throw new Error('This article is not yet published and cannot be accessed');
                    }
                                        
                    // Use the passed current news immediately
                    setNewsData(passedCurrentNews);
                    setLoading(false);
                    
                    // We still need to fetch navigation data in background
                    setNavigationLoading(true);
                    
                    // Fetch just the navigation data in background
                    try {
                        const response = await apiHelper.fetchJSONData('getAllNews');
                        if (response && response.news && Array.isArray(response.news)) {
                            setAllNewsData(response.news);
                            setupNavigation(response.news, passedCurrentNews);
                        }
                        setNavigationLoading(false);
                    } catch (navError) {
                        setNavigationLoading(false);
                    }
                    
                    return;
                }
                
                if (passedData && !passedCurrentNews) {
                    
                    
                    // We have the full data, just need to find current article
                    const currentNews = findNewsById(passedData, newsId, articleType);
                    
                    if (currentNews) {
                        // Check if the article is published
                        if (currentNews.published === false) {
                            throw new Error('This article is not yet published and cannot be accessed');
                        }
                        
                        setNewsData(currentNews);
                        setAllNewsData(passedData);
                        setLoading(false);
                        setNavigationLoading(false);
                        setupNavigation(passedData, currentNews);
                        return;
                    } else {
                        // Article not found in passed data, will fetch fresh from all sources
                    }
                }
                
                // No passed data or current article not found, fetch fresh
                await fetchFreshData();
                
            } catch (err) {
                setError(err.message);
                setLoading(false);
                setNavigationLoading(false);
                console.error('Error fetching news data:', err);
            }
        };



        if (newsId) {
            fetchNewsData();
        } else {
            setError('No news ID provided');
            setLoading(false);
            setNavigationLoading(false);
        }
    }, [newsId, articleType, location.state, retryAttempt]);

    // Handle doc parameter for legacy GUID links
    useEffect(() => {
        const docParam = searchParams.get('doc');
        
        if (docParam && newsData) {
            
            
                         // Wait a bit for the page content to render and repository links to be processed
             const timeout = setTimeout(() => {
                 // Look for repository links that contain this filename
                 const repositoryLinks = document.querySelectorAll('.repository-document-link, a[data-doc-filename], a[data-doc-url]');
                 
                 
                 
                 // Try to find a matching link by filename
                 let matchingLink = null;
                 let allMatches = [];
                 
                 repositoryLinks.forEach((link, index) => {
                     const linkFilename = link.getAttribute('data-doc-filename') || '';
                     const linkTitle = link.textContent || '';
                     const linkUrl = link.getAttribute('data-doc-url') || link.getAttribute('href') || '';
                     
                     
                     
                     // Check if the filename matches (be more specific)
                     const filenameMatch = linkFilename && docParam && (
                         linkFilename === docParam || // Exact match first
                         (linkFilename.length > 10 && docParam.includes(linkFilename)) || // Long filename contained in docParam
                         (docParam.length > 10 && linkFilename.includes(docParam)) // docParam contained in filename
                     );
                     
                     if (filenameMatch) {
                         allMatches.push({
                             link,
                             filename: linkFilename,
                             title: linkTitle,
                             url: linkUrl,
                             matchType: 'filename'
                         });
                         
                         if (!matchingLink) {
                             matchingLink = link;
                             
                         }
                     }
                 });
                 
                 
                
                // If we found a matching link, trigger the click to open PDF modal
                if (matchingLink) {
                    
                    
                    // Extract document info for the modal
                    const docTitle = matchingLink.getAttribute('data-doc-title') || docParam;
                    const docUrl = matchingLink.getAttribute('data-doc-url') || matchingLink.getAttribute('data-original-href') || '';
                    const docFilename = matchingLink.getAttribute('data-doc-filename') || docParam;
                    const docId = matchingLink.getAttribute('data-doc-id') || 'legacy';
                    
                    // Set up the repository PDF data and open modal
                    const pdfData = {
                        id: docId,
                        title: docTitle,
                        url: docUrl,
                        filename: docFilename
                    };
                    
                    setRepositoryPdfData(pdfData);
                    setShowRepositoryPdfModal(true);
                    
                    // Highlight the link temporarily
                    matchingLink.style.background = '#fff3cd';
                    matchingLink.style.border = '2px solid #ffc107';
                    matchingLink.style.borderRadius = '4px';
                    matchingLink.style.padding = '2px 4px';
                    
                    setTimeout(() => {
                        matchingLink.style.background = '';
                        matchingLink.style.border = '';
                        matchingLink.style.borderRadius = '';
                        matchingLink.style.padding = '';
                    }, 3000);
                    
                } else {
                    
                    
                    // Fallback: try to open the PDF directly if we have enough info
                    if (docParam.includes('.pdf')) {
                        const pdfData = {
                            id: 'legacy-direct',
                            title: docParam.replace('.pdf', ''),
                            url: docParam, // May not work, but worth trying
                            filename: docParam
                        };
                        
                        setRepositoryPdfData(pdfData);
                        setShowRepositoryPdfModal(true);
                    }
                }
                
                // Clean up the URL by removing the doc parameter after processing
                const newUrl = new URL(window.location);
                newUrl.searchParams.delete('doc');
                window.history.replaceState({}, '', newUrl.pathname + newUrl.search);
                
            }, 1000); // Wait 1 second for content to render
            
            return () => clearTimeout(timeout);
        }
    }, [newsData, searchParams, setRepositoryPdfData, setShowRepositoryPdfModal]);

    // Image loading logic
    const getRandomImage = () => {
        return `/assets/img/news/weekly/${Math.floor(Math.random() * 30) + 1}.jpg`;
    };

    useEffect(() => {
        if (!newsData) return;

        if (image) {
            if (image.startsWith('blob:')) {
                setArticleImage(image);
                return;
            }

            const loadImage = async () => {
                if (image.includes('assets/img/news/weekly/')) {
                    setArticleImage(image);
                    return;
                }

                if (image.includes('https://')) {
                    setArticleImage(image);
                    return;
                }

                const authToken = await apiHelper.getAuthToken();
                const imageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${image}`;
                try {
                    const response = await fetch(imageUrl, {
                        headers: { 'Authorization': `Bearer ${authToken}` }
                    });

                    if (response.ok) {
                        const blob = await response.blob();
                        setArticleImage(URL.createObjectURL(blob));
                    } else {
                        setArticleImage(getRandomImage());
                    }
                } catch {
                    setArticleImage(getRandomImage());
                }
            };

            loadImage();
        } else {
            setArticleImage(getRandomImage());
        }
    }, [image, newsData]);

    // PDF loading logic
    useEffect(() => {
        const loadPdf = async () => {
            if (file) {
                
                if (file.includes('https://')) {
                    // Check if it's actually a PDF file
                    const isPdfFile = file.toLowerCase().includes('.pdf') ||
                        file.toLowerCase().includes('pdf') ||
                        file.includes('application/pdf');

                    if (isPdfFile) {
                        
                        setPdfSource(file);
                    } else {
                        
                        // For non-PDF URLs, we can't embed them due to CSP restrictions
                        // Set to null to show the fallback message
                        setPdfSource(null);
                    }
                } else {
                    try {
                        let response;
                        try {
                            response = await apiHelper.fetchPDFNameAndContentByTitle(file);
                        } catch (err) {
                            // Fallback to the direct URL approach
                            const authToken = await apiHelper.getAuthToken();
                            const pdfUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${file}`;

                            const fetchResponse = await fetch(pdfUrl, {
                                headers: { 'Authorization': `Bearer ${authToken}` }
                            });

                            if (!fetchResponse.ok) throw new Error('Failed to fetch PDF');

                            const blob = await fetchResponse.blob();
                            response = { fileContent: blob };
                        }

                        if (response?.fileContent) {
                            const pdfBlob = new Blob([response.fileContent], { type: 'application/pdf' });
                            const pdfBlobUrl = URL.createObjectURL(pdfBlob);
                            setPdfSource(pdfBlobUrl);
                        } else {
                            throw new Error('No PDF content received');
                        }

                    } catch (err) {
                        setPdfSource(null);
                        console.error('Error loading PDF:', err);
                    }
                }
            }
        };

        loadPdf();

        return () => {
            if (pdfSource && pdfSource.startsWith('blob:')) {
                URL.revokeObjectURL(pdfSource);
            }
        };
    }, [file]);

    // Video loading logic
    useEffect(() => {
        const loadVideo = async () => {
            if (loadingRef.current || !video) return;

            loadingRef.current = true;
            setIsVideoLoading(true);
            
            if (video.includes('https://')) {
                // Check if it's a YouTube or Vimeo URL that needs iframe embedding
                const isYouTube = video.includes('youtube.com') || video.includes('youtu.be');
                const isVimeo = video.includes('vimeo.com');

                if (isYouTube || isVimeo) {
                    // For YouTube/Vimeo, we'll set a flag to use iframe instead of video element
                    setVideoSource(getEmbeddableVideoUrl(video));
                    setIsVideoEmbedded(true);
                } else {
                    // For other HTTPS URLs, use them directly
                    setVideoSource(video);
                    setIsVideoEmbedded(false);
                }

                setIsVideoLoading(false);
                loadingRef.current = false;
            } else {
                // For local videos, use streaming endpoint instead of downloading entire file
                const streamingUrl = `${process.env.REACT_APP_API_URL}media/stream/${video}`;
                
                // Test if the streaming URL is accessible
                try {
                    const testResponse = await fetch(streamingUrl, { 
                        method: 'HEAD',
                        headers: {
                            'Range': 'bytes=0-1'
                        }
                    });
                    
                    if (testResponse.ok || testResponse.status === 206) {
                        setVideoSource(streamingUrl);
                        setIsVideoEmbedded(false);
                    } else {
                        // Fallback to the old blob method
                        const { fileContent } = await apiHelper.fetchVideoByName(video);
                        const newBlobUrl = URL.createObjectURL(fileContent);
                        currentBlobUrl.current = newBlobUrl;
                        setVideoSource(newBlobUrl);
                        setIsVideoEmbedded(false);
                    }
                } catch (error) {
                    // Fallback to the old blob method
                    try {
                        const { fileContent } = await apiHelper.fetchVideoByName(video);
                        const newBlobUrl = URL.createObjectURL(fileContent);
                        currentBlobUrl.current = newBlobUrl;
                        setVideoSource(newBlobUrl);
                        setIsVideoEmbedded(false);
                    } catch (blobError) {
                        // Both streaming and blob download failed
                    }
                }
                
                setIsVideoLoading(false);
                loadingRef.current = false;
            }
        };

        loadVideo();

        return () => {
            if (currentBlobUrl.current) {
                URL.revokeObjectURL(currentBlobUrl.current);
                currentBlobUrl.current = null;
            }
            loadingRef.current = false;
        };
    }, [video]);

    // Podcast loading logic
    useEffect(() => {
        const loadPodcast = async () => {
            if (podcast) {
                if (podcast.includes('https://')) {

                    // Check if it's a platform URL that needs iframe embedding
                    const isSpotify = podcast.includes('spotify.com');
                    const isSoundCloud = podcast.includes('soundcloud.com');
                    const isAnchor = podcast.includes('anchor.fm');
                    const isPodLink = podcast.includes('pod.link');

                    if (isSpotify || isSoundCloud || isAnchor || isPodLink) {
                        // For podcast platforms, use iframe embedding
                        setPodcastSource(getEmbeddablePodcastUrl(podcast));
                        setIsPodcastEmbedded(true);

                        // Set a timeout to hide loading spinner if iframe doesn't load
                        setTimeout(() => {
                            setIsAudioLoading(false);
                        }, 5000);
                    } else {
                        // For direct audio file URLs, use audio element
                        setPodcastSource(podcast);
                        setIsPodcastEmbedded(false);
                    }
                }
                else {
                    try {
                        const authToken = await apiHelper.getAuthToken();
                        const podcastUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${podcast}`;
                        const headers = {
                            'Authorization': `Bearer ${authToken}`
                        };

                        const response = await fetch(podcastUrl, { headers });
                        if (!response.ok) throw new Error('Failed to fetch podcast');

                        const blob = await response.blob();
                        const objectUrl = URL.createObjectURL(blob);
                        setPodcastSource(objectUrl);
                        setIsPodcastEmbedded(false);

                    } catch (err) {
                        setPodcastSource(null);
                        console.error('Error loading podcast:', err);
                    }
                }
            }
        };

        loadPodcast();

        return () => {
            if (podcastSource && podcastSource.startsWith('blob:')) {
                URL.revokeObjectURL(podcastSource);
            }
        };
    }, [podcast]);

    // Helper functions for video and podcast URLs
    const getEmbeddableVideoUrl = (url) => {
        if (url.includes('youtube.com/watch?v=')) {
            const videoId = url.split('v=')[1].split('&')[0];
            return `https://www.youtube.com/embed/${videoId}`;
        } else if (url.includes('youtu.be/')) {
            const videoId = url.split('youtu.be/')[1].split('?')[0];
            return `https://www.youtube.com/embed/${videoId}`;
        } else if (url.includes('vimeo.com/')) {
            const videoId = url.split('vimeo.com/')[1].split('?')[0];
            return `https://player.vimeo.com/video/${videoId}`;
        }
        return url; // Return original URL if not YouTube/Vimeo
    };

    const getEmbeddablePodcastUrl = (url) => {
        if (url.includes('spotify.com/episode/')) {
            // Convert Spotify episode URL to embed format
            const episodeId = url.split('episode/')[1].split('?')[0];
            return `https://open.spotify.com/embed/episode/${episodeId}`;
        } else if (url.includes('spotify.com/show/')) {
            // Convert Spotify show URL to embed format
            const showId = url.split('show/')[1].split('?')[0];
            return `https://open.spotify.com/embed/show/${showId}`;
        } else if (url.includes('soundcloud.com/')) {
            // For SoundCloud, we can try to use their oEmbed API or use the URL directly
            // SoundCloud embed URLs typically need to be constructed differently
            return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`;
        } else if (url.includes('anchor.fm/')) {
            // Anchor.fm embed format
            return url.replace('anchor.fm/', 'anchor.fm/embed/');
        }
        return url; // Return original URL if not a known podcast platform
    };

    // Video click handler
    const handleVideoClick = useCallback(() => {
        setIsVideoExpanded(!isVideoExpanded);
    }, [isVideoExpanded]);

    // Image click handler
    const handleImageClick = useCallback(() => {
        setShowImageModal(true);
    }, []);

    // ESC key handler for video compression and modal closing
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                if (isVideoExpanded) {
                    setIsVideoExpanded(false);
                }
                if (showImageModal) {
                    setShowImageModal(false);
                }
            }
        };

        // Add event listener when video is expanded or image modal is open
        if (isVideoExpanded || showImageModal) {
            document.addEventListener('keydown', handleEscapeKey);
        }

        // Cleanup
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isVideoExpanded, showImageModal]);

    // Helper functions
    const hasDescriptionContent = (description, title) => {
        if (!description) return false;

        const normalizeText = (text) => {
            return text
                .replace(/[''`´]/g, "'")
                .replace(/\u2019/g, "'")
                .replace(/[""]/g, '"')
                .replace(/\u0027/g, "'")
                .replace(/&#39;/g, "'")
                .replace(/&apos;/g, "'")
                .toLowerCase()
                .trim();
        };

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = addTargetBlankToLinks(cleanJson(description));

        if (title) {
            const titleWords = normalizeText(title.split(" ").slice(0, 3).join(" "));
            const boldElements = tempDiv.querySelectorAll('b, strong, span[style*="font-weight: bold"], span[style*="font-weight:bold"], strong[style], p strong, p[style*="font-weight: bold"]');

            let titleFound = false;
            boldElements.forEach((boldElement) => {
                if (!titleFound) {
                    const boldText = normalizeText(boldElement.textContent || '');
                    const boldWords = boldText.split(" ").slice(0, 3).join(" ");

                    const isMatch = boldText.includes(titleWords) ||
                        titleWords.includes(boldText) ||
                        boldWords === titleWords;

                    if (isMatch) {
                        titleFound = true;
                        let elementToRemove = boldElement;

                        if (boldElement.tagName === 'STRONG' && boldElement.parentElement && boldElement.parentElement.tagName === 'P') {
                            const pElement = boldElement.parentElement;
                            const pContent = pElement.innerHTML.replace(boldElement.outerHTML, '').trim();
                            if (pContent === '' || pContent.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                                elementToRemove = pElement;
                            }
                        }

                        if (elementToRemove.parentElement && elementToRemove.parentElement.tagName === 'DIV') {
                            const divElement = elementToRemove.parentElement;
                            const divContent = divElement.innerHTML.replace(elementToRemove.outerHTML, '').trim();
                            if (divContent === '' || divContent.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                                elementToRemove = divElement;
                            }
                        }

                        elementToRemove.remove();
                    }
                }
            });
        }

        const finalTextContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
        return finalTextContent.length > 0;
    };

    // Event handlers
    const handleNavigation = (targetNewsId) => {
        if (targetNewsId && allNewsData.length > 0) {
            // Find the target news item to pass along
            const targetNews = allNewsData.find(item => {
                const itemId = item.id;
                
                // Try exact match first
                if (itemId === targetNewsId) return true;
                
                // Try string vs number conversion
                if (itemId === parseInt(targetNewsId)) return true;
                if (String(itemId) === String(targetNewsId)) return true;
                
                return false;
            });

            // Determine article type for URL with comprehensive detection
            let urlType = 'weekly'; // default to weekly
            if (targetNews) {
                if (targetNews.isSubmittedArticle === true) {
                    urlType = 'submitted';
                } else if (targetNews.isWeeklyNews === true) {
                    urlType = 'weekly';
                } else if (targetNews.isSubmittedArticle === false && targetNews.isWeeklyNews === false) {
                    // If both are false, check other indicators
                    if (targetNews.author || targetNews.submittedAuthor || targetNews.submittedCompany) {
                        urlType = 'submitted';
                    } else if (targetNews.weeklyCategory || targetNews.weeklySection) {
                        urlType = 'weekly';
                    }
                    // Otherwise keep 'weekly' as default
                }
            }
            
            // Store title in sessionStorage for WelcomeHeader to use immediately
            if (targetNews && targetNews.title) {
                sessionStorage.setItem(`news_title_${targetNewsId}`, targetNews.title);
            }
            
            // Pass the data to avoid re-fetching
            navigate(`/news/${urlType}/${targetNewsId}`, {
                state: {
                    allNewsData: allNewsData,
                    currentNews: targetNews
                }
            });
        } else if (targetNewsId) {
            // Fallback if no data available - default to weekly
            navigate(`/news/weekly/${targetNewsId}`);
        }
    };

    const handleBackToNewsRoom = () => {
        navigate('/newsroom');
    };

    const handlePdfModalOpen = () => {
        setShowPdfModal(true);
    };

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopyUrlText('Copied!');
            setTimeout(() => setCopyUrlText('Copy URL'), 2000);
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = window.location.href;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopyUrlText('Copied!');
                setTimeout(() => setCopyUrlText('Copy URL'), 2000);
            } catch (fallbackErr) {
                setCopyUrlText('Copy failed');
                setTimeout(() => setCopyUrlText('Copy URL'), 2000);
            }
            document.body.removeChild(textArea);
        }
    };

    // Handle repository document link clicks
    const handleRepositoryDocumentClick = (event, linkElement) => {
        try {
            event.preventDefault();
            event.stopPropagation();

            const docId = linkElement.getAttribute('data-doc-id');
            const docTitle = linkElement.getAttribute('data-doc-title');
            const docUrl = linkElement.getAttribute('data-doc-url');
            const docFilename = linkElement.getAttribute('data-doc-filename');

            // Try to get the full URL from data-full-url first, fallback to data-doc-url, then href
            const fullUrl = linkElement.getAttribute('data-full-url') ||
                linkElement.getAttribute('data-doc-url') ||
                linkElement.getAttribute('data-original-href') ||
                linkElement.getAttribute('href');

            // Set up the repository PDF data and open modal
            const pdfData = {
                id: docId || 'unknown',
                title: docTitle || 'Unknown Document',
                url: fullUrl || docUrl || 'unknown-url',
                filename: docFilename || 'unknown.pdf'
            };

            setRepositoryPdfData(pdfData);
            setShowRepositoryPdfModal(true);

        } catch (error) {
            // Error in handleRepositoryDocumentClick
        }
    };

    // Set up repository link processing after content is rendered
    useEffect(() => {
        if (!newsData) return;

        let globalHandlerRef = null;

        const processRepositoryLinks = () => {
            // Find all links in the article description
            const allLinks = document.querySelectorAll('.article-description a');

            // Find repository links AND news document links
            const repositoryLinks = Array.from(allLinks).filter(link => {
                const href = link.getAttribute('href') || '';
                const dataOriginalHref = link.getAttribute('data-original-href') || '';
                const urlToCheck = href || dataOriginalHref;

                // Check if it's already been processed - if so, it's definitely a document link
                if (link.hasAttribute('data-processed') || 
                    link.classList.contains('repository-document-link') || 
                    link.classList.contains('news-document-link')) {
                    return true;
                }

                // Check for news document links (new format)
                if (urlToCheck.includes('/files/detail/news/') && urlToCheck.includes('.pdf')) {
                    return true;
                }

                // Check URL patterns for repository links - but exclude external URLs (starting with http:// or https://)
                const isExternalUrl = urlToCheck.startsWith('http://') || urlToCheck.startsWith('https://');
                
                if (isExternalUrl) {
                    // Allow news document links even if they're external URLs
                    if (urlToCheck.includes('/files/detail/news/')) {
                        return true;
                    }
                    // But exclude other external URLs from repository link processing
                    return false;
                }
                
                const isRepositoryByUrl = urlToCheck.includes('/assets/') ||
                    urlToCheck.includes('CBI%') ||
                    urlToCheck.includes('CBI ') ||
                    urlToCheck.includes('.pdf') ||
                    urlToCheck.includes('sharepoint.com') ||
                    /\.(pdf|docx?|xlsx?|pptx?)(\?|$|%)/i.test(urlToCheck);

                return isRepositoryByUrl;
            });

            repositoryLinks.forEach((link, index) => {
                // Skip if already processed to avoid double-processing
                if (link.hasAttribute('data-processed')) {    
                    return;
                }

                const href = link.getAttribute('href') || link.getAttribute('data-original-href') || '';
                const isNewsDocumentLink = href.includes('/files/detail/news/') || link.classList.contains('news-document-link');

                // Method 1: Remove href to prevent navigation
                const originalHref = href;
                link.setAttribute('data-original-href', originalHref);
                link.removeAttribute('href');

                // Extract document info from the URL and text
                let filename, title, articleId;
                
                if (isNewsDocumentLink) {
                    // Handle news document links: /files/detail/news/12345/document.pdf
                    filename = link.getAttribute('data-filename') || href.split('/').pop() || '';
                    title = link.getAttribute('data-doc-title') || link.textContent || decodeURIComponent(filename);
                    articleId = link.getAttribute('data-article-id') || href.match(/\/files\/detail\/news\/(\d+)\//)?.[1] || '';
                } else {
                    // Handle repository links (existing logic)
                    filename = href.split('/').pop() || '';
                    title = link.textContent || decodeURIComponent(filename);
                }

                const decodedFilename = decodeURIComponent(filename);

                // Add our data attributes for the PDFModal
                link.setAttribute('data-doc-url', originalHref);
                link.setAttribute('data-doc-title', title);
                link.setAttribute('data-doc-filename', decodedFilename);
                link.setAttribute('data-full-url', originalHref);
                
                // Add news-specific attributes if applicable
                if (isNewsDocumentLink && articleId) {
                    link.setAttribute('data-article-id', articleId);
                }

                // Add click handler directly to each link
                const handleClick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    handleRepositoryDocumentClick(e, link);
                    return false;
                };

                // Remove any existing click handlers and add our new one
                link.onclick = null;
                link.removeEventListener('click', handleClick, true);
                link.removeEventListener('click', handleClick, false);
                link.addEventListener('click', handleClick, true);
                link.addEventListener('click', handleClick, false);

                // Override the click property directly
                link.onclick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRepositoryDocumentClick(e, link);
                    return false;
                };

                // Ensure the link has the right attributes for styling
                if (isNewsDocumentLink) {
                    link.classList.add('news-document-link');
                } else {
                link.classList.add('repository-document-link');
                }

                // Add visual indicator that this is processed
                link.style.position = 'relative';
                link.setAttribute('data-processed', 'true');
            });

            // Global handler for any missed repository links
            if (!globalHandlerRef) {
                const globalHandler = (e) => {
                    // Don't interfere with clicks inside PDFModal
                    let checkTarget = e.target;
                    while (checkTarget && checkTarget !== document.body) {
                        if (checkTarget.classList && (
                            checkTarget.classList.contains('pdf-modal') ||
                            checkTarget.classList.contains('pdf-modal-overlay') ||
                            checkTarget.closest('.pdf-modal')
                        )) {
                            return;
                        }
                        checkTarget = checkTarget.parentElement;
                    }

                    let target = e.target;
                    for (let i = 0; i < 5 && target; i++) {
                        if (target.tagName === 'A') {
                            const href = target.getAttribute('href') || target.getAttribute('data-original-href') || '';
                            
                            // Exclude external URLs from repository link processing
                            const isExternalUrl = href.startsWith('http://') || href.startsWith('https://');
                            if (isExternalUrl) {
                                return; // Let external URLs behave normally
                            }
                            
                            const isRepositoryLink = href.includes('/assets/') ||
                                href.includes('CBI%') ||
                                href.includes('CBI ') ||
                                href.includes('.pdf') ||
                                href.includes('sharepoint.com') ||
                                target.hasAttribute('data-doc-url') ||
                                target.classList.contains('repository-document-link');

                            const isNewsDocumentLink = href.includes('/files/detail/news/') ||
                                target.classList.contains('news-document-link');

                            if (isRepositoryLink || isNewsDocumentLink) {
                                e.preventDefault();
                                e.stopPropagation();
                                e.stopImmediatePropagation();
                                handleRepositoryDocumentClick(e, target);
                                return false;
                            }
                        }
                        target = target.parentElement;
                    }
                };

                globalHandlerRef = globalHandler;
                document.addEventListener('click', globalHandler, true);
            }
        };

        // Process links with multiple attempts to ensure they're caught
        setTimeout(processRepositoryLinks, 50);
        setTimeout(processRepositoryLinks, 200);
        setTimeout(processRepositoryLinks, 500);

        // Set up MutationObserver to handle dynamic content changes
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    setTimeout(processRepositoryLinks, 50);
                }
            });
        });

        // Observe changes in the entire news page
        const newsPageElement = document.querySelector('.news-page-container');
        if (newsPageElement) {
            observer.observe(newsPageElement, {
                childList: true,
                subtree: true
            });
        }

        return () => {
            observer.disconnect();
            if (globalHandlerRef) {
                document.removeEventListener('click', globalHandlerRef, true);
                globalHandlerRef = null;
            }
        };
    }, [newsData]);

    // Loading state
    if (loading) {
        return (
            <div className="news-page-container">
                <div className="container mt-6">
                    <LoadingSpinner text="Loading News Article..." />
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        const isUnpublishedError = error.includes('not yet published');
        const isNotFoundError = error.includes('not found') || error.includes('News item with ID');
        
        return (
            <div className="news-page-container">
                <div className="container mt-6">
                    <div className="error-container">
                        <h2>{isUnpublishedError ? 'Article Not Available' : 'News Article Not Found'}</h2>
                        {isUnpublishedError ? (
                            <div>
                                <p><i className="fas fa-clock"></i> This article is not yet published and cannot be accessed.</p>
                                <p>Please check back later or browse our available news articles.</p>
                            </div>
                        ) : (
                            <div>
                                <p>{error}</p>
                                {isNotFoundError && (
                                    <div style={{ marginTop: '15px' }}>
                                        {retryAttempt > 0 && (
                                            <p style={{ color: '#6c757d', fontSize: '0.9em', marginBottom: '10px' }}>
                                                <i className="fas fa-info-circle"></i> 
                                                {retryAttempt === 1 ? ' First retry attempt failed.' : ` ${retryAttempt} retry attempts failed.`}
                                                {retryAttempt < 3 ? ' This might be a temporary sync issue.' : ' This appears to be a persistent issue.'}
                                            </p>
                                        )}
                                        <p style={{ color: '#495057', fontSize: '0.95em', marginBottom: '15px' }}>
                                            Sometimes articles take a moment to sync. You can try refreshing the data:
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="error-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {isNotFoundError && (
                                <button 
                                    className="btn btn-outline-success"
                                    onClick={handleRetry}
                                    disabled={isRetrying || retryAttempt >= 5}
                                    title={retryAttempt >= 5 ? "Maximum retry attempts reached" : "Try to reload the article data"}
                                >
                                    {isRetrying ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Retrying... (Attempt {retryAttempt + 1})
                                        </>
                                    ) : retryAttempt >= 5 ? (
                                        <>
                                            <i className="fas fa-exclamation-triangle"></i> Max Retries Reached
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-refresh"></i> Try Again {retryAttempt > 0 ? `(${retryAttempt + 1}/5)` : ''}
                                        </>
                                    )}
                                </button>
                            )}
                            <button className="btn btn-primary" onClick={handleBackToNewsRoom}>
                                <i className="fas fa-arrow-left"></i> News Room
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="news-page-container">
            {/* Navigation Header */}
            <div className="news-page-header">
                <div className="container">
                    <div className="news-page-nav">
                        <button className="btn btn-outline-primary" onClick={handleBackToNewsRoom}>
                            <i className="fas fa-arrow-left"></i> News Room
                        </button>
                        
                        <div className="news-page-nav-controls">
                            {navigationLoading && (
                                <div className="navigation-loading" style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#6c757d', fontSize: '0.875rem' }}>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Loading navigation...
                                </div>
                            )}
                            

                            
                            {!navigationLoading && previousNewsId && (() => {
                                // Filter navigation data to exclude intro articles and unpublished articles (same logic as setupNavigation)
                                const navigationNews = allNewsData.filter(item => {
                                    const isIntro = (item.title && item.title.toLowerCase().includes('intro')) ||
                                                  (item.weeklyCategory && item.weeklyCategory.toLowerCase() === 'intro') ||
                                                  (item.title === 'Intro');
                                    const isUnpublished = item.published === false;
                                    return !isIntro && !isUnpublished;
                                });
                                
                                const prevArticle = navigationNews.find(item => item.id === previousNewsId);
                                const prevSummary = prevArticle?.summary || '';
                                const cleanSummary = (() => {
                                    // Create a temporary div to decode HTML entities
                                    const tempDiv = document.createElement('div');
                                    tempDiv.innerHTML = prevSummary.replace(/<[^>]*>/g, ''); // Remove HTML tags first
                                    let decoded = tempDiv.textContent || tempDiv.innerText || '';
                                    // Additional cleanup for common entities that might not decode properly
                                    decoded = decoded.replace(/&nbsp;/g, ' ')
                                                   .replace(/&#160;/g, ' ')
                                                   .replace(/&amp;/g, '&')
                                                   .replace(/&lt;/g, '<')
                                                   .replace(/&gt;/g, '>')
                                                   .replace(/&quot;/g, '"')
                                                   .replace(/&#39;/g, "'")
                                                   .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                                                   .trim();
                                    return decoded.substring(0, 200) + (decoded.length > 200 ? '...' : '');
                                })();
                                
                                return (
                                    <Tippy
                                        content={
                                            <div className="nav-tooltip-content">
                                                <div className="nav-tooltip-title">{prevArticle?.title}</div>
                                                {cleanSummary && (
                                                    <div className="nav-tooltip-summary">{cleanSummary}</div>
                                                )}
                                                <div className="nav-tooltip-meta">
                                                    {prevArticle?.author && (
                                                        <span><i className="fas fa-user"></i> {prevArticle.author}</span>
                                                    )}
                                                    {(() => {
                                                        if (!prevArticle?.portalPublishDate) return null;
                                                        
                                                        // Parse DD/MM/YYYY format
                                                        const dateString = prevArticle.portalPublishDate;
                                                        const dateParts = dateString.split('/');
                                                        
                                                        if (dateParts.length !== 3) return null;
                                                        
                                                        const [day, month, year] = dateParts;
                                                        const dateObj = new Date(year, month - 1, day);
                                                        
                                                        if (isNaN(dateObj.getTime())) return null;
                                                        
                                                        return (
                                                            <span><i className="fas fa-calendar"></i> {dateObj.toLocaleDateString('en-GB', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric'
                                                            })}</span>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        }
                                        placement="bottom"
                                        interactive={true}
                                        delay={[500, 200]}
                                        maxWidth={350}
                                        theme="light-border"
                                    >
                                        <button 
                                            className="btn btn-outline-secondary nav-preview-btn"
                                            onClick={() => handleNavigation(previousNewsId)}
                                        >
                                            <div className="nav-preview-content">
                                                <div className="nav-direction">
                                                    <i className="fas fa-chevron-left"></i> Previous
                                                </div>
                                                {prevArticle && (
                                                    <div className="nav-preview-title">
                                                        {prevArticle.title?.length > 40 
                                                            ? prevArticle.title.substring(0, 40) + '...' 
                                                            : prevArticle.title}
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    </Tippy>
                                );
                            })()}
                            
                            {!navigationLoading && nextNewsId && (() => {
                                // Filter navigation data to exclude intro articles and unpublished articles (same logic as setupNavigation)
                                const navigationNews = allNewsData.filter(item => {
                                    const isIntro = (item.title && item.title.toLowerCase().includes('intro')) ||
                                                  (item.weeklyCategory && item.weeklyCategory.toLowerCase() === 'intro') ||
                                                  (item.title === 'Intro');
                                    const isUnpublished = item.published === false;
                                    return !isIntro && !isUnpublished;
                                });
                                
                                const nextArticle = navigationNews.find(item => item.id === nextNewsId);
                                const nextSummary = nextArticle?.summary || '';
                                const cleanSummary = (() => {
                                    // Create a temporary div to decode HTML entities
                                    const tempDiv = document.createElement('div');
                                    tempDiv.innerHTML = nextSummary.replace(/<[^>]*>/g, ''); // Remove HTML tags first
                                    let decoded = tempDiv.textContent || tempDiv.innerText || '';
                                    // Additional cleanup for common entities that might not decode properly
                                    decoded = decoded.replace(/&nbsp;/g, ' ')
                                                   .replace(/&#160;/g, ' ')
                                                   .replace(/&amp;/g, '&')
                                                   .replace(/&lt;/g, '<')
                                                   .replace(/&gt;/g, '>')
                                                   .replace(/&quot;/g, '"')
                                                   .replace(/&#39;/g, "'")
                                                   .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                                                   .trim();
                                    return decoded.substring(0, 200) + (decoded.length > 200 ? '...' : '');
                                })();
                                
                                return (
                                    <Tippy
                                        content={
                                            <div className="nav-tooltip-content">
                                                <div className="nav-tooltip-title">{nextArticle?.title}</div>
                                                {cleanSummary && (
                                                    <div className="nav-tooltip-summary">{cleanSummary}</div>
                                                )}
                                                <div className="nav-tooltip-meta">
                                                    {nextArticle?.author && (
                                                        <span><i className="fas fa-user"></i> {nextArticle.author}</span>
                                                    )}
                                                    {(() => {
                                                        if (!nextArticle?.portalPublishDate) return null;
                                                        
                                                        // Parse DD/MM/YYYY format
                                                        const dateString = nextArticle.portalPublishDate;
                                                        const dateParts = dateString.split('/');
                                                        
                                                        if (dateParts.length !== 3) return null;
                                                        
                                                        const [day, month, year] = dateParts;
                                                        const dateObj = new Date(year, month - 1, day);
                                                        
                                                        if (isNaN(dateObj.getTime())) return null;
                                                        
                                                        return (
                                                            <span><i className="fas fa-calendar"></i> {dateObj.toLocaleDateString('en-GB', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric'
                                                            })}</span>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        }
                                        placement="bottom"
                                        interactive={true}
                                        delay={[500, 200]}
                                        maxWidth={350}
                                        theme="light-border"
                                    >
                                        <button 
                                            className="btn btn-outline-secondary nav-preview-btn"
                                            onClick={() => handleNavigation(nextNewsId)}
                                        >
                                            <div className="nav-preview-content">
                                                <div className="nav-direction">
                                                    Next <i className="fas fa-chevron-right"></i>
                                                </div>
                                                {nextArticle && (
                                                    <div className="nav-preview-title">
                                                        {nextArticle.title?.length > 40 
                                                            ? nextArticle.title.substring(0, 40) + '...' 
                                                            : nextArticle.title}
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    </Tippy>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mt-4">
                <div className="news-page-content">
                    {/* Metadata Section */}
                    <div className="news-page-metadata-section">
                        <div className="metadata-row-primary">
                            {/* Article Type Badge */}
                            <div className="metadata-badges">
                                {(() => {
                                    const isSubmitted = Boolean(newsData?.isSubmittedArticle) || 
                                                       newsData?.isSubmittedArticle === 'true' || 
                                                       newsData?.isSubmittedArticle === 1;
                                    
                                    const isWeekly = Boolean(newsData?.isWeeklyNews) || 
                                                    newsData?.isWeeklyNews === 'true' || 
                                                    newsData?.isWeeklyNews === 1;
                                    
                                    let articleTypeBadge = 'Weekly News';
                                    let badgeClass = 'badge-weekly';
                                    
                                    if (isSubmitted) {
                                        articleTypeBadge = 'Submitted Article';
                                        badgeClass = 'badge-submitted';
                                    } else if (isWeekly) {
                                        articleTypeBadge = 'Weekly News';
                                        badgeClass = 'badge-weekly';
                                    } else {
                                        // Fallback detection
                                        const hasAuthor = newsData?.author || newsData?.submittedAuthor || newsData?.submittedCompany;
                                        const hasWeeklyProps = newsData?.weeklyCategory || newsData?.weeklySection;
                                        
                                        if (hasAuthor && !hasWeeklyProps) {
                                            articleTypeBadge = 'Submitted Article';
                                            badgeClass = 'badge-submitted';
                                        }
                                    }
                                    
                                    return (
                                        <span className={`article-type-badge ${badgeClass}`}>
                                            {articleTypeBadge}
                                        </span>
                                    );
                                })()}
                                
                                {/* Category Badge */}
                                {(newsData?.weeklyCategory || newsData?.weeklySection) && (
                                    <span className={`category-badge ${replaceCss(newsData.weeklyCategory || newsData.weeklySection)}`}>
                                        {newsData.weeklyCategory || newsData.weeklySection}
                                    </span>
                                )}
                            </div>
                            
                            {/* Reading Time */}
                            <div className="reading-time">
                                <i className="fas fa-clock"></i>
                                {calculateReadingTime(description)} min read
                            </div>
                        </div>
                        
                        <div className="metadata-row-secondary">
                            {/* Publication Date */}
                            {(() => {
                                if (!newsData?.portalPublishDate) return null;
                                
                                // Parse DD/MM/YYYY format
                                const dateString = newsData.portalPublishDate;
                                const dateParts = dateString.split('/');
                                
                                if (dateParts.length !== 3) return null;
                                
                                const [day, month, year] = dateParts;
                                const dateObj = new Date(year, month - 1, day);
                                
                                if (isNaN(dateObj.getTime())) return null;
                                
                                return (
                                    <div className="publish-date">
                                        <i className="fas fa-calendar-alt"></i>
                                        Published: {dateObj.toLocaleDateString('en-GB', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </div>
                                );
                            })()}
                            
                            {/* Author Info */}
                           
                            {((Boolean(newsData?.isSubmittedArticle) || newsData?.isSubmittedArticle === 'true' || newsData?.isSubmittedArticle === 1) || (author || firm)) && (
                                <div className="author-info">
                                    <i className="fas fa-user"></i>
                                    {author && <span className="author-name">{author}</span>}
                                    {author && firm && <span className="author-divider">|</span>}
                                    {firm && <span className="author-company">{firm}</span>}                                    
                                </div>
                            )}
                        </div>
                        

                    </div>
                    
                    {title && (
                        <div className="news-page-title-section">
                            <h2 className="news-page-content-title">{title}</h2>
                        </div>
                    )}
                    
                    {/* Summary Section - only show if we have a separate summary */}
                    {summary && (
                        <div className="article-summary-section">
                            <div className="article-summary">
                                <div dangerouslySetInnerHTML={{ __html: cleanJson(addTargetBlankToLinks(summary)) }} />
                            </div>
                        </div>
                    )}

                    <div className="news-article-layout">
                        <div className="article-content-left">
                            {description && (
                                <div className="article-description description">
                                    {(() => {
                                        try {
                                           
                                            
                                            // Ensure description is a string
                                            const descStr = typeof description === 'string' ? description : 
                                                          typeof description === 'object' ? JSON.stringify(description) : 
                                                          String(description || '');
                                            
                                         
                                            
                                            const cleaned = cleanJson(descStr);
                                            
                                            const withLinks = addTargetBlankToLinks(cleaned);
                                         
                                            
                                                                            // Instead of using removeFirstBoldTag which returns React components,
                                // let's implement the title removal logic inline to return HTML string
                                const final = (() => {
                                    if (!title) return withLinks;
                                    
                                    const normalizeText = (text) => {
                                        return text
                                            .replace(/[''`´]/g, "'")
                                            .replace(/\u2019/g, "'")
                                            .replace(/[""]/g, '"')
                                            .replace(/\u0027/g, "'")
                                            .replace(/&#39;/g, "'")
                                            .replace(/&apos;/g, "'")
                                            .toLowerCase()
                                            .trim();
                                    };
                                    
                                    const titleWords = normalizeText(title.split(" ").slice(0, 3).join(" "));
                                    const tempDiv = document.createElement('div');
                                    tempDiv.innerHTML = withLinks;
                                    
                                    const boldElements = tempDiv.querySelectorAll('b, strong, span[style*="font-weight: bold"], span[style*="font-weight:bold"], strong[style], p strong, p[style*="font-weight: bold"]');
                                    
                                    let titleFound = false;
                                    boldElements.forEach((boldElement) => {
                                        if (!titleFound) {
                                            const boldText = normalizeText(boldElement.textContent || '');
                                            const boldWords = boldText.split(" ").slice(0, 3).join(" ");
                                            
                                            const isMatch = boldText.includes(titleWords) ||
                                                           titleWords.includes(boldText) ||
                                                           boldWords === titleWords;
                                            
                                            if (isMatch) {
                                                titleFound = true;
                                                let elementToRemove = boldElement;
                                                
                                                if (boldElement.tagName === 'STRONG' && boldElement.parentElement && boldElement.parentElement.tagName === 'P') {
                                                    const pElement = boldElement.parentElement;
                                                    const pContent = pElement.innerHTML.replace(boldElement.outerHTML, '').trim();
                                                    if (pContent === '' || pContent.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                                                        elementToRemove = pElement;
                                                    }
                                                }
                                                
                                                if (elementToRemove.parentElement && elementToRemove.parentElement.tagName === 'DIV') {
                                                    const divElement = elementToRemove.parentElement;
                                                    const divContent = divElement.innerHTML.replace(elementToRemove.outerHTML, '').trim();
                                                    if (divContent === '' || divContent.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                                                        elementToRemove = divElement;
                                                    }
                                                }
                                                
                                                elementToRemove.remove();
                                            }
                                        }
                                    });
                                    
                                    // Clean up empty elements
                                    const emptyParagraphs = tempDiv.querySelectorAll('p');
                                    emptyParagraphs.forEach(p => {
                                        const content = p.innerHTML.trim();
                                        if (content === '<br>' || content === '<br/>' || content === '<br />' ||
                                            content === '&nbsp;' || content === '' || content === ' ' ||
                                            content === '&nbsp;&nbsp;' || content.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                                            p.remove();
                                        }
                                    });
                                    
                                    const emptyDivs = tempDiv.querySelectorAll('div');
                                    emptyDivs.forEach(div => {
                                        const content = div.innerHTML.trim();
                                        if (content === '<br>' || content === '<br/>' || content === '<br />' ||
                                            content === '&nbsp;' || content === '' || content === ' ' ||
                                            content === '&nbsp;&nbsp;' || content.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                                            div.remove();
                                        }
                                    });
                                    
                                    return tempDiv.innerHTML;
                                })();
                                
             
                                
                                // Double check that final is a string
                                if (typeof final !== 'string') {
                                    throw new Error(`Final result is not a string: ${typeof final}`);
                                }
                                            
                                            return (
                                                <div dangerouslySetInnerHTML={{ __html: final }} />
                                            );
                                        } catch (error) {
                                            // Fallback: show raw content safely
                                            const rawText = typeof description === 'string' ? description : JSON.stringify(description, null, 2);
                                            return (
                                                <div>
                                                    <p><strong>Error displaying formatted content. Raw content:</strong></p>
                                                    <pre style={{ whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: '10px' }}>
                                                        {rawText}
                                                    </pre>
                                                </div>
                                            );
                                        }
                                    })()}
                                </div>
                            )}

                            {file && (
                                <div className="article-pdf">
                                    {pdfSource ? (
                                        <div className="pdf-iframe-container">
                                            <iframe
                                                src={pdfSource}
                                                title={`PDF: ${title}`}
                                                className="pdf-iframe"
                                            />
                                        </div>
                                    ) : (
                                        <div style={{ padding: '20px', background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px' }}>
                                            <i className="fas fa-exclamation-triangle"></i>
                                            {file.includes('https://') ? (
                                                <>
                                                    This document cannot be embedded due to security restrictions.
                                                    <br />
                                                    <a
                                                        href={file}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: '#856404', textDecoration: 'underline', fontWeight: 'bold' }}
                                                    >
                                                        <i className="fas fa-external-link-alt"></i> Open in new tab
                                                    </a>
                                                </>
                                            ) : (
                                                'PDF is loading...'
                                            )}
                                        </div>
                                    )}

                                    {file && !file.includes('https://') && (
                                        <div className="pdf-header" style={{ marginTop: '10px' }}>
                                            <button
                                                className="pdf-open-modal-btn"
                                                onClick={handlePdfModalOpen}
                                                title="Open PDF in window"
                                            >
                                                <i className="fas fa-expand-arrows-alt"></i> Open PDF
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="article-media-right">
                            {articleImage && (
                                <div className="article-image compact">
                                    <div className="image-container" onClick={handleImageClick} title="Click to view image in window">
                                        <img src={articleImage} alt={title} />
                                        <div className="image-overlay">
                                            <i className="fas fa-expand"></i>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {podcast && (
                                <div className="article-audio">
                                    {isAudioLoading && (
                                        <div className="media-loading">
                                            <LoadingSpinner />
                                        </div>
                                    )}
                                    {isPodcastEmbedded ? (
                                        <iframe
                                            src={podcastSource}
                                            title={`Podcast: ${title}`}
                                            className="podcast-player"
                                            frameBorder="0"
                                            allow="encrypted-media"
                                            style={{ width: '100%', height: '152px', display: isAudioLoading ? 'none' : 'block' }}
                                            onLoad={() => {
                                                setIsAudioLoading(false);
                                            }}
                                            onError={() => {
                                                setIsAudioLoading(false);
                                            }}
                                        />
                                    ) : (
                                        <audio
                                            className="audio"
                                            src={podcastSource}
                                            controls
                                            onLoadStart={() => setIsAudioLoading(true)}
                                            onCanPlay={() => setIsAudioLoading(false)}
                                            style={{ display: isAudioLoading ? 'none' : 'block' }}
                                        />
                                    )}
                                </div>
                            )}

                            {video && (
                                <div className={`article-video ${isVideoExpanded ? 'expanded' : 'compact'}`}>
                                    {isVideoExpanded && (
                                        <button 
                                            className="video-close-btn"
                                            onClick={handleVideoClick}
                                            title="Close video"
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    )}
                                    {isVideoLoading ? (
                                        <LoadingSpinner />
                                    ) : (
                                        <>
                                            <div className="video-container">
                                                {isVideoEmbedded ? (
                                                    <iframe
                                                        src={videoSource}
                                                        title={title}
                                                        className="video-player"
                                                        frameBorder="0"
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                        allowFullScreen
                                                    />
                                                ) : (
                                                    <video
                                                        src={videoSource}
                                                        controls
                                                        className="video-player"
                                                        onError={(e) => {
                                                            // Video error occurred
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            
                                            <div className="video-controls">
                                                <button 
                                                    className="video-expand-btn"
                                                    onClick={handleVideoClick}
                                                    title={isVideoExpanded ? "Compress video" : "Expand video"}
                                                >
                                                    <i className={`fas fa-${isVideoExpanded ? 'compress' : 'expand'}`}></i>
                                                    {isVideoExpanded ? ' Compress' : ' Expand'}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Member Tools Section - Bottom of Article */}
                    <div className="member-tools-section">
                        <div className="member-tools-container">
                            <div className="member-actions">
                                <span className="actions-label">Member Tools:</span>
                                <button 
                                    className="action-btn action-copy"
                                    onClick={handleCopyUrl}
                                    title="Copy URL to share with other members"
                                >
                                    <i className="fas fa-link"></i>
                                    <span className="action-text">{copyUrlText}</span>
                                </button>
                                <button 
                                    className="action-btn action-email"
                                    onClick={() => {
                                        const subject = encodeURIComponent(`Member Portal Article: ${title}`);
                                        const body = encodeURIComponent(`Hi,\n\nI thought you might find this article from our member portal interesting:\n\n${title}\n\n${window.location.href}\n\nBest regards`);
                                        window.location.href = `mailto:?subject=${subject}&body=${body}`;
                                    }}
                                    title="Email to fellow members"
                                >
                                    <i className="fas fa-envelope"></i>
                                    <span className="action-text">Email Link</span>
                                </button>
                                <button 
                                    className="action-btn action-print"
                                    onClick={() => window.print()}
                                    title="Print article"
                                >
                                    <i className="fas fa-print"></i>
                                    <span className="action-text">Print</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* PDF Modal */}
            {showPdfModal && (
                <PDFModal
                    key={newsData.id}
                    externalUrls={newsData.externalUrls}
                    title={newsData.title}
                    url={newsData.file}
                    show={showPdfModal}
                    onClose={() => setShowPdfModal(false)}
                    allowFullscreen={true}
                    allowMove={true}
                    showLinkinToolBar={true}
                />
            )}

            {showRepositoryPdfModal && repositoryPdfData && (
                <PDFModal
                    key={`repo-${repositoryPdfData.id}`}
                    title={repositoryPdfData.title}
                    url={repositoryPdfData.url}
                    show={showRepositoryPdfModal}
                    onClose={() => {
                        setShowRepositoryPdfModal(false);
                        setRepositoryPdfData(null);
                    }}
                    allowFullscreen={true}
                    allowMove={true}
                    showLinkinToolBar={true}
                    listItemId={repositoryPdfData.id}
                    externalUrls={false}
                />
            )}

            {/* Image Modal */}
            {showImageModal && articleImage && (
                <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
                    <div className="image-modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="image-modal-header">
                            <h3>
                                <Tippy content={title}>
                                    <span>{title.length > 50 ? `${title.substring(0, 50)}...` : title}</span>
                                </Tippy>
                            </h3>
                            <button 
                                className="image-modal-close"
                                onClick={() => setShowImageModal(false)}
                                title="Close image"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="image-modal-content">
                            <img src={articleImage} alt={title} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default News; 